import { create } from "zustand";
import axios from "../lib/axios";
import toast from "react-hot-toast";

export const useReviewStore = create((set, get) => ({
	// State
	reviews: [],
	userReviews: [],
	productStats: null,
	loading: false,
	error: null,
	currentPage: 1,
	totalPages: 1,
	totalReviews: 0,
	hasNext: false,
	hasPrev: false,

	// Actions
	
	/**
	 * יצירת ביקורת חדשה
	 */
	createReview: async (reviewData) => {
		set({ loading: true, error: null });
		try {
			const response = await axios.post("/reviews", reviewData);
			
			if (response.data.success) {
				toast.success("ביקורת נוצרה בהצלחה וממתינה לאישור! 🌟");
				
				// הוספה למערך ביקורות המשתמש
				set((state) => ({
					userReviews: [response.data.review, ...state.userReviews],
					loading: false
				}));
				
				return response.data.review;
			}
		} catch (error) {
			const errorMessage = error?.response?.data?.message || error?.message || "שגיאה ביצירת הביקורת";
			toast.error(errorMessage);
			set({ error: errorMessage, loading: false });
			throw error;
		}
	},

	/**
	 * קבלת ביקורות של מוצר
	 */
	getProductReviews: async (productId, options = {}) => {
		set({ loading: true, error: null });
		try {
			const queryParams = new URLSearchParams({
				page: options.page || 1,
				limit: options.limit || 10,
				sortBy: options.sortBy || 'newest',
				filterBy: options.filterBy || 'all',
				minRating: options.minRating || 1,
				maxRating: options.maxRating || 5
			});

			const response = await axios.get(`/reviews/product/${productId}?${queryParams}`);
			
			if (response.data.success) {
				set({
					reviews: response.data.reviews,
					productStats: response.data.stats,
					currentPage: response.data.pagination.currentPage,
					totalPages: response.data.pagination.totalPages,
					totalReviews: response.data.pagination.totalReviews,
					hasNext: response.data.pagination.hasNext,
					hasPrev: response.data.pagination.hasPrev,
					loading: false
				});
			}
		} catch (error) {
			const errorMessage = error?.response?.data?.message || error?.message || "שגיאה בקבלת ביקורות";
			set({ error: errorMessage, loading: false });
			toast.error(errorMessage);
		}
	},

	/**
	 * הצבעה על ביקורת
	 */
	voteOnReview: async (reviewId, voteType) => {
		try {
			const response = await axios.post(`/reviews/${reviewId}/vote`, { voteType });
			
			if (response.data.success) {
				// עדכון הביקורת במערך
				set((state) => ({
					reviews: state.reviews.map(review => 
						review._id === reviewId 
							? {
								...review,
								helpfulVotes: response.data.helpfulVotes,
								notHelpfulVotes: response.data.notHelpfulVotes,
								helpfulnessPercentage: response.data.helpfulnessPercentage
							}
							: review
					)
				}));
				
				toast.success("הצבעתך נרשמה! 👍");
			}
		} catch (error) {
			const errorMessage = error?.response?.data?.message || error?.message || "שגיאה בהצבעה";
			toast.error(errorMessage);
		}
	},

	/**
	 * עדכון ביקורת
	 */
	updateReview: async (reviewId, updateData) => {
		set({ loading: true, error: null });
		try {
			const response = await axios.put(`/reviews/${reviewId}`, updateData);
			
			if (response.data.success) {
				toast.success("ביקורת עודכנה בהצלחה! ממתינה לאישור מחדש 📝");
				
				// עדכון הביקורת במערכים
				set((state) => ({
					reviews: state.reviews.map(review => 
						review._id === reviewId ? response.data.review : review
					),
					userReviews: state.userReviews.map(review => 
						review._id === reviewId ? response.data.review : review
					),
					loading: false
				}));
				
				return response.data.review;
			}
		} catch (error) {
			const errorMessage = error?.response?.data?.message || error?.message || "שגיאה בעדכון הביקורת";
			toast.error(errorMessage);
			set({ error: errorMessage, loading: false });
			throw error;
		}
	},

	/**
	 * מחיקת ביקורת
	 */
	deleteReview: async (reviewId) => {
		try {
			const response = await axios.delete(`/reviews/${reviewId}`);
			
			if (response.data.success) {
				toast.success("ביקורת נמחקה בהצלחה 🗑️");
				
				// הסרה מהמערכים
				set((state) => ({
					reviews: state.reviews.filter(review => review._id !== reviewId),
					userReviews: state.userReviews.filter(review => review._id !== reviewId),
					totalReviews: state.totalReviews - 1
				}));
			}
		} catch (error) {
			const errorMessage = error?.response?.data?.message || error?.message || "שגיאה במחיקת הביקורת";
			toast.error(errorMessage);
		}
	},

	/**
	 * קבלת ביקורות של משתמש
	 */
	getUserReviews: async (userId, options = {}) => {
		set({ loading: true, error: null });
		try {
			const queryParams = new URLSearchParams({
				page: options.page || 1,
				limit: options.limit || 10
			});

			const response = await axios.get(`/reviews/user/${userId}?${queryParams}`);
			
			if (response.data.success) {
				set({
					userReviews: response.data.reviews,
					currentPage: response.data.pagination.currentPage,
					totalPages: response.data.pagination.totalPages,
					totalReviews: response.data.pagination.totalReviews,
					hasNext: response.data.pagination.hasNext,
					hasPrev: response.data.pagination.hasPrev,
					loading: false
				});
			}
		} catch (error) {
			const errorMessage = error?.response?.data?.message || error?.message || "שגיאה בקבלת ביקורות המשתמש";
			set({ error: errorMessage, loading: false });
			toast.error(errorMessage);
		}
	},

	/**
	 * דיווח על ביקורת
	 */
	reportReview: async (reviewId, reason) => {
		try {
			// TODO: יישום דיווח על ביקורת
			console.log('Reporting review:', reviewId, 'for reason:', reason);
			toast.success("דיווח נשלח בהצלחה! נבדוק את הביקורת 🚨");
		} catch (error) {
			const errorMessage = error?.response?.data?.message || error?.message || "שגיאה בשליחת הדיווח";
			toast.error(errorMessage);
		}
	},

	/**
	 * העלאת תמונות לביקורת
	 */
	uploadReviewImages: async (reviewId, files) => {
		try {
			const formData = new FormData();
			files.forEach(file => {
				formData.append('images', file);
			});

			const response = await axios.post(`/reviews/${reviewId}/images`, formData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				}
			});
			
			if (response.data.success) {
				toast.success("תמונות הועלו בהצלחה! 📷");
				return response.data.images;
			}
		} catch (error) {
			const errorMessage = error?.response?.data?.message || error?.message || "שגיאה בהעלאת התמונות";
			toast.error(errorMessage);
			throw error;
		}
	},

	// Actions for Admin

	/**
	 * קבלת ביקורות ממתינות (אדמין)
	 */
	getPendingReviews: async (options = {}) => {
		set({ loading: true, error: null });
		try {
			const queryParams = new URLSearchParams({
				page: options.page || 1,
				limit: options.limit || 10
			});

			const response = await axios.get(`/reviews/pending?${queryParams}`);
			
			if (response.data.success) {
				set({
					reviews: response.data.reviews,
					currentPage: response.data.pagination.currentPage,
					totalPages: response.data.pagination.totalPages,
					totalReviews: response.data.pagination.totalReviews,
					hasNext: response.data.pagination.hasNext,
					hasPrev: response.data.pagination.hasPrev,
					loading: false
				});
			}
		} catch (error) {
			const errorMessage = error?.response?.data?.message || error?.message || "שגיאה בקבלת ביקורות ממתינות";
			set({ error: errorMessage, loading: false });
			toast.error(errorMessage);
		}
	},

	/**
	 * אישור/דחיית ביקורת (אדמין)
	 */
	moderateReview: async (reviewId, status, reason = "") => {
		try {
			const response = await axios.patch(`/reviews/${reviewId}/status`, { status, reason });
			
			if (response.data.success) {
				const statusText = status === 'approved' ? 'אושרה' : 
				                 status === 'rejected' ? 'נדחתה' : 'הוסתרה';
				toast.success(`ביקורת ${statusText} בהצלחה! ✅`);
				
				// עדכון הביקורת במערך
				set((state) => ({
					reviews: state.reviews.map(review => 
						review._id === reviewId 
							? { ...review, status }
							: review
					)
				}));
			}
		} catch (error) {
			const errorMessage = error?.response?.data?.message || error?.message || "שגיאה בניהול הביקורת";
			toast.error(errorMessage);
		}
	},

	/**
	 * הוספת תגובת אדמין
	 */
	addAdminResponse: async (reviewId, content) => {
		try {
			const response = await axios.post(`/reviews/${reviewId}/admin-response`, { content });
			
			if (response.data.success) {
				toast.success("תגובה נוספה בהצלחה! 💬");
				
				// עדכון הביקורת במערך
				set((state) => ({
					reviews: state.reviews.map(review => 
						review._id === reviewId 
							? { ...review, adminResponse: response.data.review.adminResponse }
							: review
					)
				}));
			}
		} catch (error) {
			const errorMessage = error?.response?.data?.message || error?.message || "שגיאה בהוספת תגובה";
			toast.error(errorMessage);
		}
	},

	// Utility functions

	/**
	 * איפוס הסטייט
	 */
	resetState: () => {
		set({
			reviews: [],
			userReviews: [],
			productStats: null,
			loading: false,
			error: null,
			currentPage: 1,
			totalPages: 1,
			totalReviews: 0,
			hasNext: false,
			hasPrev: false
		});
	},

	/**
	 * הגדרת עמוד נוכחי
	 */
	setCurrentPage: (page) => {
		set({ currentPage: page });
	},

	/**
	 * חישוב ממוצע דירוגים
	 */
	calculateAverageRating: () => {
		const { reviews } = get();
		if (!reviews || reviews.length === 0) return 0;
		
		const totalRating = reviews.reduce((sum, review) => sum + review.ratings.overall, 0);
		return Math.round((totalRating / reviews.length) * 10) / 10;
	},

	/**
	 * סינון ביקורות לפי דירוג
	 */
	filterByRating: (rating) => {
		const { reviews } = get();
		return reviews.filter(review => Math.round(review.ratings.overall) === rating);
	},

	/**
	 * קבלת התפלגות דירוגים
	 */
	getRatingDistribution: () => {
		const { reviews } = get();
		const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
		
		reviews.forEach(review => {
			const rating = Math.round(review.ratings.overall);
			distribution[rating]++;
		});
		
		return distribution;
	}
})); 